import {
  Record as RaRecord,
  SortPayload,
  useDataProvider,
  useListFilterContext,
} from 'react-admin';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Autocomplete } from '@material-ui/lab';

import { useLazyReferenceOptions } from 'hooks';
import {
  LAZY_AUTOCOMPLETE_STYLE,
  LazyReferenceTextField,
  OptionText,
  optionLabelOf,
  sameOptionBy,
} from 'components/LazyReferenceAutocomplete';

interface LazyReferenceInputMultiFilterProps {
  label: string;
  reference: string;
  referenceForGetOne?: string;
  source: string;
  multipleFilters: (search: string) => Record<string, unknown>[];
  sort?: SortPayload;
  perPage?: number;
  optionText?: OptionText;
  optionValue?: string;
  debounceTime?: number;
  'data-cy'?: string;
  // Read by react-admin's <Filter>; not forwarded to the Autocomplete.
  alwaysOn?: boolean;
  allowEmpty?: boolean;
  resettable?: boolean;
}

const compareBy =
  ({ field, order }: SortPayload) =>
  (a: RaRecord, b: RaRecord) => {
    const fieldA = a[field];
    const fieldB = b[field];

    if (fieldA == null) return 1;
    if (fieldB == null) return -1;

    const comparison = fieldA < fieldB ? -1 : fieldA > fieldB ? 1 : 0;
    return order === 'ASC' ? comparison : -comparison;
  };

export const LazyReferenceInputMultiFilter = ({
  label,
  reference,
  referenceForGetOne,
  source,
  multipleFilters,
  sort = { field: 'name', order: 'ASC' },
  perPage = 25,
  optionText = 'name',
  optionValue = 'id',
  debounceTime = 500,
  'data-cy': dataCy,
}: LazyReferenceInputMultiFilterProps) => {
  const dataProvider = useDataProvider();
  const { setFilters, filterValues, displayedFilters } = useListFilterContext();

  const { open, setOpen, options, loading, setInputValue } =
    useLazyReferenceOptions({
      label,
      debounceTime,
      loadOptions: search =>
        Promise.all(
          multipleFilters(search).map(filter =>
            dataProvider.getList(reference, {
              pagination: { page: 1, perPage },
              sort,
              filter,
            }),
          ),
        ).then(responses => {
          const byId = new Map(
            responses
              .flatMap(response => response.data)
              .map(item => [item[optionValue], item]),
          );
          return Array.from(byId.values()).sort(compareBy(sort));
        }),
    });

  const labelOf = useMemo(() => optionLabelOf(optionText), [optionText]);

  const [selectedOption, setSelectedOption] = useState<RaRecord | null>(null);
  const lastFilterValue = useRef(filterValues[source]);
  const hasLoadedInitialValue = useRef(false);

  // Load initial value from filter if present (when navigating back to a list with filters)
  useEffect(() => {
    const currentFilterValue = filterValues[source];

    // If filter was cleared, reset the flag and clear selection
    if (!currentFilterValue && hasLoadedInitialValue.current) {
      hasLoadedInitialValue.current = false;
      if (selectedOption) {
        setSelectedOption(null);
        setInputValue('');
      }
      return;
    }

    // Only load initial value once when component mounts with a filter value
    if (
      currentFilterValue &&
      !hasLoadedInitialValue.current &&
      !selectedOption
    ) {
      hasLoadedInitialValue.current = true;

      let cancelled = false;

      // Use referenceForGetOne if provided, otherwise use reference
      const getOneReference = referenceForGetOne || reference;

      dataProvider
        .getOne(getOneReference, { id: currentFilterValue })
        .then(({ data }) => {
          // Only update state if the request wasn't cancelled and filter still has value
          if (!cancelled && filterValues[source] === currentFilterValue) {
            setSelectedOption(data);
            setInputValue(labelOf(data));
          }
        })
        .catch(err => {
          if (!cancelled) {
            console.error(
              '[LazyReferenceInputMultiFilter] Error loading initial value:',
              err,
            );
          }
        });

      return () => {
        cancelled = true;
      };
    }
  }, [
    filterValues,
    source,
    reference,
    referenceForGetOne,
    dataProvider,
    labelOf,
    selectedOption,
    setInputValue,
  ]);

  // Sync with filter changes (when filter is cleared externally)
  useEffect(() => {
    const currentFilterValue = filterValues[source];

    // Only act if the filter value actually changed
    if (currentFilterValue !== lastFilterValue.current) {
      lastFilterValue.current = currentFilterValue;

      // If filter was cleared but we still have a selectedOption, clear it
      if (!currentFilterValue && selectedOption) {
        setSelectedOption(null);
        setInputValue('');
      }
    }
  }, [filterValues, source, selectedOption, setInputValue]);

  return (
    <Autocomplete
      data-cy={dataCy}
      style={LAZY_AUTOCOMPLETE_STYLE}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={labelOf}
      getOptionSelected={sameOptionBy(optionValue)}
      loading={loading}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      }}
      value={selectedOption}
      onChange={(_, newValue) => {
        setSelectedOption(newValue);
        setInputValue(labelOf(newValue));

        // Update filters
        const newFilters = { ...filterValues };
        if (newValue) {
          newFilters[source] = newValue[optionValue];
        } else {
          delete newFilters[source];
        }

        setFilters(newFilters, displayedFilters, false);
      }}
      renderInput={params => (
        <LazyReferenceTextField
          params={params}
          label={label}
          loading={loading}
        />
      )}
    />
  );
};
